import Button from "components/shared/button";
import { Popup } from "components/popups/Popup";
import React, { useEffect, useState } from "react";
import Input from "components/shared/inputs/input";
import useTranslation from "utils/hooks/useTranslation";
import useCustomForm from "utils/hooks/useCustomForm";
import { useCreateDiscountManagementMutation, useUpdateDiscountManagementMutation } from "store/api";
import type { DatasourceMutationRequest } from "./TvaPopup";

type EditableDiscountManagementRow = {
  id?: number | string;
  key?: number | string;
  start_date?: string;
  end_date?: string;
  heatpump_env_code?: string;
  text?: string;
};

type DiscountManagementPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  request: DatasourceMutationRequest | null;
  envNumeric: number | undefined;
  mode?: "create" | "edit";
  row?: EditableDiscountManagementRow | null;
  onSaved?: () => Promise<void> | void;
};

const toIsoDate = (value?: string) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const [datePart] = raw.split("T");
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;

  const [y, m, d] = raw.split(/[/-]/).map(Number);
  if (!y || !m || !d) return "";
  return new Date(Date.UTC(y, m - 1, d)).toISOString().split("T")[0];
};

const dateToUtcIso = (value: string) => {
  const [y, m, d] = String(value).split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(Date.UTC(y, m - 1, d)).toISOString();
};

const DiscountManagementPopup: React.FC<DiscountManagementPopupProps> = ({
  isOpen,
  onClose,
  request,
  envNumeric,
  mode = "edit",
  row,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [createDiscountManagement] = useCreateDiscountManagementMutation();
  const [updateDiscountManagement] = useUpdateDiscountManagementMutation();
  const isEditMode = mode === "edit";

  const { control, reset, getValues } = useCustomForm<{
    start_date: string;
    end_date: string;
    heatpump_env_code: string;
    text: string;
  }>({
    defaultValues: {
      start_date: "",
      end_date: "",
      heatpump_env_code: "",
      text: "",
    },
  });

  useEffect(() => {
    setIsSaving(false);
    reset({
      start_date: isOpen ? toIsoDate(row?.start_date) : "",
      end_date: isOpen ? toIsoDate(row?.end_date) : "",
      heatpump_env_code: isOpen ? String(row?.heatpump_env_code ?? "") : "",
      text: isOpen ? String(row?.text ?? "") : "",
    });
  }, [isOpen, reset, row?.start_date, row?.end_date, row?.heatpump_env_code, row?.text]);

  const handleSave = async () => {
    if (!request) return;
    const rowId = row?.id ?? row?.key;
    if (isEditMode && rowId == null) return;
    if (envNumeric == null || Number.isNaN(envNumeric)) return;

    const { start_date, end_date, heatpump_env_code, text } = getValues();
    const startDateIso = dateToUtcIso(start_date);
    const endDateIso = dateToUtcIso(end_date);
    if (!startDateIso || !endDateIso) return;

    // const baseUrl = request.url.replace(/\/(?:env=.*|\d+)$/, "");

    const baseUrl = request.url.replace(/\/[^/]+$/, "");
    const url = isEditMode ? `${baseUrl}/${rowId}` : baseUrl;

    const payload: Record<string, unknown> = {
      start_date: startDateIso,
      end_date: endDateIso,
      heatpump_env_code,
      text,
      environnement_id: envNumeric,
    };

    if (isEditMode) {
      payload.id = rowId;
    }

    setIsSaving(true);
    try {
      if (isEditMode) {
        await updateDiscountManagement({ url, data: payload }).unwrap();
      } else {
        await createDiscountManagement({ url, data: payload }).unwrap();
      }

      onSaved?.();
      onClose();
    } catch {
      // handled by api interceptor
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Popup
      title={isEditMode ? "Modifier discount" : "Nouveau discount"}
      onClose={onClose}
      portal
      fixSize="middle"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button type="button" primary onClick={handleSave} disabled={isSaving}>
              {t(isEditMode ? "Update" : "Add")}
            </Button>
          </group>
        ),
      }}
    >
      <group
        data-direction="column"
        data-gap="20"
        data-space="40"
        data-space-horizontal="40"
        data-space-top="5"
        data-height="autofit"
      >
        <group data-gap="10" data-space-vertical="20">
          <group data-direction="column" data-gap="20">
            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input dataLength="autofit" label={t("start_date")} name="start_date" control={control} type="date" />
              </group>
              <group data-length="forcefit" data-fit="1">
                <Input dataLength="autofit" label={t("end_date")} name="end_date" control={control} type="date" />
              </group>
            </group>

            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={t("heatpump_env_code")}
                  name="heatpump_env_code"
                  control={control}
                  type="text"
                  size="large"
                />
              </group>
            </group>

            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input dataLength="autofit" label={t("text")} name="text" control={control} type="textarea" size="large" />
              </group>
            </group>
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default DiscountManagementPopup;
